
import React, { useEffect, useState } from "react";

import { Button, Dropdown, Form, Table } from "react-bootstrap";
import { withTranslation } from "react-i18next";
import { BiDotsHorizontalRounded, BiPlus, BiSearch } from "react-icons/bi";
import { FcFinePrint } from "react-icons/fc";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Pagination from "../../../components/Pagination/Pagination";
import { DEFAULT_ITEMS_PER_PAGE } from '../../../core/constants/common';
import DashGraphConfigService from "../../../core/service/DashGraphConfigService";
import ConfirmDialog from "../../../core/utilities/ConfirmPopup";
import CreateGraphSetModal from "./CreateGraphSetModal";
import "./GraphConfigList.scss";

const GraphConfigList = ({ t, companyId = 0, standardName = '', categoryName = '', forAssignment = false }) => {
  const navigate = useNavigate();

  const [itemsTotalCount, setItemTotalCount] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_ITEMS_PER_PAGE);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageData, setPageData] = useState([]);
  const [configToDelete, setConfigToDelete] = useState(0);
  const [searchKey, setSearchKey] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [showSetModal, setShowSetModal] = useState(false);

  useEffect(() => {
    if (forAssignment) return;
    
    listConfigs();
  }, []);

  // Executed from grpah assigment to company screen
  useEffect(() => {
    if (!forAssignment || companyId <= 0) return;
    
    listConfigs();
  }, [companyId, standardName, categoryName]);

  const listConfigs = (pageNumber, itemsPerPageVal, searchKeyValue = null) => {
    if (pageNumber > 0) {
      pageNumber = pageNumber - 1;
    } else {
      setCurrentPage(1);
    }
    let perPage;
    if (itemsPerPageVal > 0) {
      perPage = itemsPerPageVal;
    } else {
      perPage = itemsPerPage;
    }
    const searchValue = searchKeyValue !== null ? searchKeyValue : searchKey;
    
    DashGraphConfigService.getConfigs(
      pageNumber,
      perPage,
      forAssignment ? companyId : null,
      forAssignment ? categoryName : null,
      forAssignment ? standardName : null,
      searchValue || null
    ).then((response) => {
      if (response.status !== 200) return;
      
      if (response.data.data) {
        setItemTotalCount(response.data.data.totalElements);
        setPageData([...response.data.data.content]);
      } else {
        setItemTotalCount(0);
        setPageData([]);
      }
    }).catch((error) => {
    });
  };

  const openConfig = (g, duplicate = false) => {
    navigate('./form', {
      state: {
        graphConfig: g,
        duplicate: duplicate
      }
    });
  };

  const onDeleteConfig = () => {
    DashGraphConfigService.deleteConfig(configToDelete)
    .then((response) => {
      setConfigToDelete(0);
      if (response.status !== 204) return;

      toast.success("Success", {
        position: toast.POSITION.TOP_RIGHT,
      });
      listConfigs();
    })
    .catch((error) => {
      setConfigToDelete(0);
    });
  };

  const changeGraphStatus = (graph) => {
    DashGraphConfigService.changeStatus(graph.id, companyId).then(res => {
      if (res.status !== 200 && res.status !== 201 && res.status !== 204) return;

      listConfigs(currentPage, itemsPerPage);
      toast.success('Success', {
        position: toast.POSITION.TOP_RIGHT,
      });
    })
    .catch((error) => {
      toast.error(error.message, {
        position: toast.POSITION.TOP_RIGHT,
      });
    });
  };

  const onAssignGraph = (graph) => {
    const payload =  { id: graph.id, sortOrder: 1 };
    DashGraphConfigService.assignToCompany(payload, companyId).then(res => {
      if (res.status !== 200 && res.status !== 201 && res.status !== 204) return;

      listConfigs(currentPage, itemsPerPage);
      toast.success('Success', {
        position: toast.POSITION.TOP_RIGHT,
      });
    })
    .catch((error) => {
      toast.error(error.message, {
        position: toast.POSITION.TOP_RIGHT,
      });
    });
  };

  const handleSearch = () => {
    setCurrentPage(1);
    listConfigs(1, itemsPerPage, searchKey);
  };

  const handleSearchKeyChange = (e) => {
    setSearchKey(e.target.value);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const clearSearch = () => {
    setSearchKey('');
    setCurrentPage(1);
    listConfigs(1, itemsPerPage, '');
  };

  const renderRowAction = (graph, index) => {
    if (forAssignment) {
      return (
        <>
          <td>
            <Form.Check
              type="switch"
              label={graph.assignedToCompany ? t('Yes.label') : t('No.label')}
              key={`assignGraphCheck-${graph.id}`}
              id={`assignGraphCheck-${graph.id}`}
              variant="outline-primary"
              checked={graph.assignedToCompany}
              onChange={() => onAssignGraph(graph)}
              />
          </td>
        </>        
      );
    } else {
      return (
        <td>
          <Dropdown>
            <Dropdown.Toggle
              variant="blank_outline"
              id="graphConfigListDropdown">
              <BiDotsHorizontalRounded />
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => setConfigToDelete(graph.id)}>
                {t("Delete.label")}
              </Dropdown.Item>
              <Dropdown.Item onClick={() => openConfig(graph, true)}>
                {t("Duplicate.label")}
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </td>
      );
    }
  };

  const toggleRowSelection = (id) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  return (
    <>
      <div className="FormsList">
        <div className="List_header mb-1">
          <h5 className="MH">{t("DashboardGraphConfig.label")}</h5>
          
        {/* Search Section */}
        {
          // !forAssignment &&
          <div className="search-section d-flex justify-content-end align-items-center ">
            <div className='searchListBox'>
              <span><BiSearch /></span>
              <input 
                type='text' 
                className='form-control' 
                placeholder={t("SearchTitle.label")} 
                value={searchKey}
                onChange={handleSearchKeyChange}
                onKeyPress={handleSearchKeyPress}
              />
            </div>
            {searchKey && (
              <Button 
                variant="outline-secondary" 
                onClick={clearSearch}
                className="ms-2"
                size="sm"
              >
                {t("Clear.label")}
              </Button>
            )}
          </div>
        }
          {
            !forAssignment &&
            <div className="List_sorts_menu">
                <ul>
                    <li>
                        <Button
                          variant="outline-primary"
                          disabled={selectedIds.length === 0}
                          onClick={() => setShowSetModal(true)}>
                          {t("CreateSet.label")}
                        </Button>
                    </li>
                    <li>
                        <Link to="./form">
                            {" "}
                            <Button variant="primary" >
                                <BiPlus />
                                {t("New.label")}
                            </Button>
                        </Link>
                    </li>
                </ul>
            </div>
          }
        </div>


        <div className="List_Table_01">          
          <div className="card List_table-card01">
            <Table responsive="sm">
              <thead>
                <tr>                  
                  <th>{t("Title.label")}</th>
                  <th>{t("Description.label")}</th>
                  <th>{t("Type.label")}</th>
                  {
                    forAssignment
                    ?
                    <>
                      <th>{t('Assigned.label')}</th>
                    </>
                    :
                    <th></th>
                  }
                </tr>
              </thead>
              <tbody>
                {pageData?.length > 0
                  ?
                  (
                    pageData?.map((graph, index) => (
                      <tr key={graph.id}>
                        <td>
                          <div className="d-flex align-items-center">
                            <Form.Check
                              type="checkbox"
                              id={`graphConfigSelect-${graph.id}`}
                              className="me-2"
                              checked={selectedIds.includes(graph.id)}
                              onChange={() => toggleRowSelection(graph.id)}
                            />
                            <a  href="#"
                              className="cursor"
                              onClick={(e) => { e.preventDefault(); if (!forAssignment) openConfig(graph); }}>
                                {graph.title}
                            </a>
                          </div>
                        </td>
                        <td>{graph.description}</td>
                        <td>{graph.type}</td>
                        {renderRowAction(graph, index)}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" className="text-center">
                        <div className="Nodatafound_box">
                          <h6><FcFinePrint className="me-2" /> {t("Nodatafound.label")}</h6>
                        </div>
                      </td>
                    </tr>
                  )}
              </tbody>
            </Table>
            <div className="p-2 List_footer_count">
            <Pagination
              itemsTotalCount={itemsTotalCount}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              alwaysShown={true}
              setItemsPerPage={setItemsPerPage}
              pageClicked={(pageNum, perPage) => {
              listConfigs(pageNum, perPage);
              }}/>
            </div>
          </div>
        </div>
      </div>
      <CreateGraphSetModal
        show={showSetModal}
        onClose={() => setShowSetModal(false)}
        graphConfigIds={selectedIds}
        onSaved={() => setSelectedIds([])}
      />
      {
				configToDelete > 0 &&
				<ConfirmDialog
					open={configToDelete > 0}
					setOpen={() => setConfigToDelete(0)}
					onConfirm={() => onDeleteConfig()}>
					<div className='text-center'>{t("ConfirmDelete.label")}</div>
				</ConfirmDialog>
			}
    </>
  );
};

export default withTranslation()(GraphConfigList);
